<template>
  <div class="rte">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="rte-toolbar" role="toolbar" aria-label="Formato de texto">
        <div class="rte-group">
          <button type="button" class="rte-btn" title="Deshacer" aria-label="Deshacer" @click="commands.undo">
            <span class="material-symbols-outlined">undo</span>
          </button>
          <button type="button" class="rte-btn" title="Rehacer" aria-label="Rehacer" @click="commands.redo">
            <span class="material-symbols-outlined">redo</span>
          </button>
        </div>

        <div class="rte-group">
          <button
            type="button"
            class="rte-btn"
            :class="{ 'is-active': isActive.bold() }"
            title="Negrita"
            aria-label="Negrita"
            @click="commands.bold"
          >
            <span class="material-symbols-outlined">format_bold</span>
          </button>
          <button
            type="button"
            class="rte-btn"
            :class="{ 'is-active': isActive.italic() }"
            title="Cursiva"
            aria-label="Cursiva"
            @click="commands.italic"
          >
            <span class="material-symbols-outlined">format_italic</span>
          </button>
          <button
            type="button"
            class="rte-btn"
            :class="{ 'is-active': isActive.underline() }"
            title="Subrayado"
            aria-label="Subrayado"
            @click="commands.underline"
          >
            <span class="material-symbols-outlined">format_underlined</span>
          </button>
        </div>

        <div class="rte-group">
          <button
            v-for="a in alineaciones"
            :key="a.value"
            type="button"
            class="rte-btn"
            :title="a.label"
            :aria-label="a.label"
            @click="commands.paragraph({ textAlign: a.value })"
          >
            <span class="material-symbols-outlined">{{ a.icon }}</span>
          </button>
        </div>

        <div class="rte-group">
          <select v-model="selectedFont" class="rte-select" aria-label="Tamaño de letra" @change="fontSelected(selectedFont)">
            <option :value="null">Tamaño</option>
            <option v-for="f in fontSizes" :key="f" :value="f">{{ f }}</option>
          </select>
          <select v-model="selectedFamily" class="rte-select rte-select--wide" aria-label="Tipo de letra" @change="fontFamilySelected(selectedFamily)">
            <option :value="null">Tipo de letra</option>
            <option v-for="f in fontFamilies" :key="f.value" :value="f.value">{{ f.text }}</option>
          </select>
        </div>

        <div class="rte-group">
          <div class="rte-color">
            <button
              type="button"
              class="rte-btn"
              title="Color del texto"
              aria-label="Color del texto"
              :aria-expanded="paleta === 'texto'"
              @click="togglePaleta('texto')"
            >
              <span class="material-symbols-outlined">format_color_text</span>
              <span class="rte-color-bar" :style="{ background: selectedColor }"></span>
            </button>
            <div v-if="paleta === 'texto'" class="rte-palette">
              <button
                v-for="(color, index) in colors"
                :key="'t' + index"
                type="button"
                class="rte-swatch"
                :style="{ background: color }"
                :title="color"
                @click="colorSelected(color)"
              ></button>
            </div>
          </div>
          <div class="rte-color">
            <button
              type="button"
              class="rte-btn"
              title="Color de fondo"
              aria-label="Color de fondo"
              :aria-expanded="paleta === 'fondo'"
              @click="togglePaleta('fondo')"
            >
              <span class="material-symbols-outlined">format_ink_highlighter</span>
              <span class="rte-color-bar" :style="{ background: selectedBackground }"></span>
            </button>
            <div v-if="paleta === 'fondo'" class="rte-palette">
              <button
                v-for="(color, index) in colors"
                :key="'f' + index"
                type="button"
                class="rte-swatch"
                :style="{ background: color }"
                :title="color"
                @click="backgroundSelected(color)"
              ></button>
            </div>
          </div>
        </div>

        <div class="rte-group">
          <button type="button" class="rte-btn" title="Insertar imagen" aria-label="Insertar imagen" @click="$bvModal.show(idSelector)">
            <span class="material-symbols-outlined">add_photo_alternate</span>
          </button>
        </div>
      </div>
    </editor-menu-bar>

    <editor-content class="rte-content" :editor="editor" />

    <selectorImagenes :id="idSelector" @imagen="imageSelected" />
  </div>
</template>

<script>
import SelectorImagenes from "./SelectorImages.vue";

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  History,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Focus,
  Bold,
  Italic,
  Underline,
} from "tiptap-extensions";

import Field from "../tiptapComponents/Field.js";
import FontSize from "../tiptapComponents/FontSize.js";
import FontFamily from "../tiptapComponents/FontFamily.js";
import FieldsList from "../tiptapComponents/FieldsList.js";
import BackgroundColor from "../tiptapComponents/BackgroundColor.js";
import TextColor from "../tiptapComponents/TextColor.js";
import Paragraph from "../tiptapComponents/Paragraph.js";
import TipTapCustomImage from "../tiptapComponents/TipTapImage.js";

let contador = 0;

export default {
  name: "editorDeTexto",

  components: {
    SelectorImagenes,
    EditorContent,
    EditorMenuBar,
  },
  props: ["htmlForEditor", "obtenerCambiosHtml"],
  data() {
    contador += 1;
    return {
      idSelector: "selectorImagenEditor-" + contador,
      htmlContent: "",
      paleta: null,
      selectedFont: null,
      previousFont: null,
      selectedFamily: null,
      previousFontFamily: null,
      selectedBackground: "rgb(255, 255, 255)",
      selectedColor: "rgb(0, 0, 0)",
      activeColor: undefined,
      activeBackground: undefined,
      alineaciones: [
        { value: "left", label: "Alinear a la izquierda", icon: "format_align_left" },
        { value: "center", label: "Centrar", icon: "format_align_center" },
        { value: "right", label: "Alinear a la derecha", icon: "format_align_right" },
      ],
      fontSizes: ["8px", "10px", "12px", "14px", "16px", "18px", "24px", "36px"],
      fontFamilies: [
        { text: "Andale Mono", value: "andale mono,times" },
        { text: "Arial", value: "arial,helvetica,sans-serif" },
        { text: "Arial Black", value: "arial black,avant garde" },
        { text: "Book Antiqua", value: "book antiqua,palatino" },
        { text: "Comic Sans MS", value: "comic sans ms,sans-serif" },
        { text: "Courier New", value: "courier new,courier" },
        { text: "Georgia", value: "georgia,palatino" },
        { text: "Helvetica", value: "helvetica" },
        { text: "Impact", value: "impact,chicago" },
        { text: "Tahoma", value: "tahoma,arial,helvetica,sans-serif" },
        { text: "Times New Roman", value: "times new roman,times" },
        { text: "Trebuchet MS", value: "trebuchet ms,geneva" },
        { text: "Verdana", value: "verdana,geneva" },
      ],
      colors: [
        "rgb(0, 0, 0)", "rgb(153, 51, 0)", "rgb(51, 51, 0)", "rgb(0, 51, 0)", "rgb(0, 51, 102)",
        "rgb(0, 0, 128)", "rgb(51, 51, 153)", "rgb(51, 51, 51)", "rgb(128, 0, 0)", "rgb(255, 102, 0)",
        "rgb(128, 128, 0)", "rgb(0, 128, 0)", "rgb(0, 128, 128)", "rgb(0, 0, 255)", "rgb(102, 102, 153)",
        "rgb(128, 128, 128)", "rgb(255, 0, 0)", "rgb(255, 153, 0)", "rgb(153, 204, 0)", "rgb(51, 153, 102)",
        "rgb(51, 204, 204)", "rgb(51, 102, 255)", "rgb(128, 0, 128)", "rgb(153, 153, 153)", "rgb(255, 0, 255)",
        "rgb(255, 204, 0)", "rgb(255, 255, 0)", "rgb(0, 255, 0)", "rgb(0, 255, 255)", "rgb(0, 204, 255)",
        "rgb(153, 51, 102)", "rgb(255, 255, 255)", "rgb(255, 153, 204)", "rgb(255, 204, 153)", "rgb(255, 255, 153)",
        "rgb(204, 255, 204)", "rgb(204, 255, 255)", "rgb(153, 204, 255)", "rgb(204, 153, 255)",
      ],
      editor: new Editor({
        extensions: [
          new History(),
          new Table(),
          new TableCell(),
          new TableHeader(),
          new TableRow(),
          new Field(),
          new Focus({
            className: "has-focus",
            nested: false,
          }),
          new Bold(),
          new Italic(),
          new Underline(),
          new FontSize(),
          new FontFamily(),
          new FieldsList(),
          new BackgroundColor(),
          new TextColor(),
          new TipTapCustomImage(),
          new Paragraph(),
        ],
        autoFocus: true,
        onUpdate: ({ getHTML }) => {
          this.htmlContent = getHTML();
        },
        content: "<p></p>",
      }),
    };
  },
  mounted() {
    this.cargarHtml(this.htmlForEditor);
    document.addEventListener("click", this.cerrarPaletaFuera);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.cerrarPaletaFuera);
    this.editor.destroy();
  },
  watch: {
    // El padre pide el HTML actual cambiando este valor
    obtenerCambiosHtml() {
      this.$emit("nuevoHtml", this.htmlContent);
    },
    htmlForEditor(value) {
      if (value !== this.htmlContent) this.cargarHtml(value);
    },
  },
  methods: {
    cargarHtml(html) {
      this.htmlContent = html || "<p></p>";
      this.editor.setContent(this.htmlContent, false);
    },
    togglePaleta(tipo) {
      this.paleta = this.paleta === tipo ? null : tipo;
    },
    cerrarPaletaFuera(e) {
      if (this.paleta && !e.target.closest(".rte-color")) this.paleta = null;
    },
    imageSelected(value) {
      if (!value || !value.length) return;
      // URL absoluta (queda guardada en el HTML de la noticia): versión grande
      const src = `${this.$media}/img/gallery/${value[0].imagen}`;
      const img = new Image();
      img.src = src;
      img.onload = () => {
        this.editor.focus();
        this.$nextTick(() => {
          this.editor.commands.image({ src, height: img.height, width: img.width });
        });
      };
    },
    fontFamilySelected(value) {
      this.editor.focus();
      if (this.previousFontFamily !== null) this.editor.commands.fontFamily({ fontFamily: this.previousFontFamily });
      this.$nextTick(() => {
        this.editor.commands.fontFamily({ fontFamily: value });
        this.previousFontFamily = value;
      });
    },
    fontSelected(value) {
      this.editor.focus();
      if (this.previousFont !== null) this.editor.commands.fontSize({ fontSize: this.previousFont });
      this.$nextTick(() => {
        this.editor.commands.fontSize({ fontSize: value });
        this.previousFont = value;
      });
    },
    backgroundSelected(val) {
      this.paleta = null;
      if (!val) return;
      this.editor.focus();
      if (this.activeBackground === val) {
        this.$nextTick(() => {
          this.editor.commands.backgroundColor({ backgroundColor: val });
          this.activeBackground = undefined;
          this.selectedBackground = "rgb(255, 255, 255)";
        });
      } else {
        if (this.activeBackground !== undefined) {
          this.editor.commands.backgroundColor({ backgroundColor: this.activeBackground });
        }
        this.selectedBackground = val;
        this.$nextTick(() => {
          this.editor.commands.backgroundColor({ backgroundColor: val });
          this.activeBackground = val;
        });
      }
    },
    colorSelected(val) {
      this.paleta = null;
      if (!val) return;
      this.editor.focus();
      if (this.activeColor === val) {
        this.$nextTick(() => {
          this.editor.commands.textColor({ textColor: val });
          this.activeColor = undefined;
          this.selectedColor = "rgb(0, 0, 0)";
        });
      } else {
        if (this.activeColor !== undefined) {
          this.editor.commands.textColor({ textColor: this.activeColor });
        }
        this.selectedColor = val;
        this.$nextTick(() => {
          this.editor.commands.textColor({ textColor: val });
          this.activeColor = val;
        });
      }
    },
  },
};
</script>

<style scoped>
.rte {
  overflow: visible;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-surface);
}

.rte:focus-within {
  border-color: var(--sd-primary);
  box-shadow: 0 0 0 3px var(--sd-primary-soft);
}

.rte-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-bottom: 1px solid var(--sd-border);
  border-radius: 12px 12px 0 0;
  background: var(--sd-surface-2);
}

.rte-group {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-right: 6px;
  margin-right: 2px;
  border-right: 1px solid var(--sd-border);
}

.rte-group:last-child {
  border-right: 0;
}

.rte-btn {
  position: relative;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--sd-text);
  cursor: pointer;
  transition: background 0.12s;
}

.rte-btn:hover {
  background: var(--sd-surface);
}

.rte-btn.is-active {
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.rte-select {
  height: 34px;
  max-width: 96px;
  padding: 0 6px;
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  background: var(--sd-surface);
  font-size: 0.8rem;
  color: var(--sd-text);
  cursor: pointer;
}

.rte-select--wide {
  max-width: 140px;
}

.rte-color {
  position: relative;
}

.rte-color-bar {
  position: absolute;
  right: 8px;
  bottom: 5px;
  left: 8px;
  height: 3px;
  border-radius: 2px;
  box-shadow: 0 0 0 1px var(--sd-border);
}

.rte-palette {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: repeat(8, 20px);
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
}

.rte-swatch {
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.1s;
}

.rte-swatch:hover {
  transform: scale(1.2);
}

.rte-content ::v-deep .ProseMirror {
  min-height: 340px;
  max-height: 60vh;
  padding: 16px 18px;
  overflow-y: auto;
  outline: 0;
  font-size: 1rem;
  line-height: 1.65;
  color: #0f172a;
  background: #fff;
  border-radius: 0 0 12px 12px;
}

.rte-content ::v-deep .ProseMirror p {
  margin: 0 0 0.8em;
}

.rte-content ::v-deep .ProseMirror img {
  max-width: 100%;
}
</style>
